import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { cn } from '@costar-ui/cosmos';
import { IconArrowCaretDown } from '@costar-ui/icons/csg/IconArrowCaretDown';
import { IconArrowCaretUp } from '@costar-ui/icons/csg/IconArrowCaretUp';
import { noop } from '@costar-ui/utils';
import React, { useMemo, useState, useCallback } from 'react';
import { composeRenderProps } from 'react-aria-components';
import { useListData } from 'react-stately';
import _Select from './aria/select.esm.js';
import _SelectValue from './aria/select-value.esm.js';
import { isSingleSelectValue, isMultiSelectValue } from './types.esm.js';
import { useComponentTheme } from '../../theme/theme.context.esm.js';
import Button from '../button/button.esm.js';
import GridList from '../collections/grid-list.esm.js';
import GridListItem from '../collections/grid-list-item.esm.js';
import Description from '../field/description.esm.js';
import FieldError from '../field/field-error/field-error.esm.js';
import Label from '../field/label/label.esm.js';
import Slot from '../layout/slot/slot.esm.js';
import { PopoverCore } from '../popover/popover-core.esm.js';
import Text from '../text/text.esm.js';

var _excluded = ["className", "description", "errorMessage", "isOpen", "items", "label", "layout", "legacyShadow", "multiple", "onChange", "defaultValue", "screenReaderOnly", "testId", "variant", "renderSelectItems", "getItemKey"];
function defaultRenderSelectItems(selectedItems) {
  var _selectedItems$value;
  if (Array.isArray(selectedItems)) {
    var _selectedItems$0$valu, _selectedItems$0$valu3;
    if (selectedItems.length > 1 && (_selectedItems$0$valu = selectedItems[0].value) !== null && _selectedItems$0$valu !== void 0 && _selectedItems$0$valu.label) {
      var _selectedItems$0$valu2;
      return /*#__PURE__*/React.createElement(Slot, {
        canShrink: true,
        fullWidth: true
      }, /*#__PURE__*/React.createElement(Text, {
        slot: "label",
        ellipsis: "1"
      }, "".concat((_selectedItems$0$valu2 = selectedItems[0].value) === null || _selectedItems$0$valu2 === void 0 ? void 0 : _selectedItems$0$valu2.label)), /*#__PURE__*/React.createElement("span", null, "\xA0", "+".concat(selectedItems.length - 1)));
    } else if (selectedItems.length === 1 && (_selectedItems$0$valu3 = selectedItems[0].value) !== null && _selectedItems$0$valu3 !== void 0 && _selectedItems$0$valu3.label) {
      return /*#__PURE__*/React.createElement(Text, {
        slot: "label",
        ellipsis: "1"
      }, "".concat(selectedItems[0].value.label));
    }
  }
  return !Array.isArray(selectedItems) && selectedItems !== null && selectedItems !== void 0 && (_selectedItems$value = selectedItems.value) !== null && _selectedItems$value !== void 0 && _selectedItems$value.label ? /*#__PURE__*/React.createElement(Text, {
    slot: "label",
    ellipsis: "1"
  }, "".concat(selectedItems.value.label)) : null;
}
function defaultGetItemKey(item) {
  return item.id;
}

/**
 * Simple dropdown select for single select.
 */
function Select(_ref) {
  var _className = _ref.className,
    description = _ref.description,
    errorMessage = _ref.errorMessage,
    _isOpen = _ref.isOpen,
    items = _ref.items,
    label = _ref.label,
    _ref$layout = _ref.layout,
    layout = _ref$layout === void 0 ? 'relative' : _ref$layout,
    legacyShadow = _ref.legacyShadow,
    multiple = _ref.multiple,
    onChange = _ref.onChange,
    defaultValue = _ref.defaultValue,
    screenReaderOnly = _ref.screenReaderOnly,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'tw-select' : _ref$testId,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'outlined' : _ref$variant,
    _ref$renderSelectItem = _ref.renderSelectItems,
    renderSelectItems = _ref$renderSelectItem === void 0 ? defaultRenderSelectItems : _ref$renderSelectItem,
    _ref$getItemKey = _ref.getItemKey,
    getItemKey = _ref$getItemKey === void 0 ? defaultGetItemKey : _ref$getItemKey,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useComponentTheme('Select');
  // note: this pattern of resolving the tailwind classes only works if it's
  // has no direct dependency on any state of renderProps from react-aria context.
  // if there are any style that are dependent on the state, we will need to use data-xxx attribute
  // that react-aria provide to manage the style change.
  var _useMemo = useMemo(function () {
      var _theme = theme({
          variant: variant,
          layout: layout,
          screenReaderOnly: screenReaderOnly
        }),
        base = _theme.base,
        label = _theme.label,
        button = _theme.button,
        subText = _theme.subText,
        listBox = _theme.listBox,
        gridListItem = _theme.gridListItem;
      return {
        baseClasses: base(),
        labelClasses: label(),
        buttonClasses: button(),
        subTextClasses: subText(),
        listBoxClasses: listBox(),
        gridListItemClasses: gridListItem()
      };
    }, [layout, screenReaderOnly, theme, variant]),
    baseClasses = _useMemo.baseClasses,
    labelClasses = _useMemo.labelClasses,
    buttonClasses = _useMemo.buttonClasses,
    subTextClasses = _useMemo.subTextClasses,
    listBoxClasses = _useMemo.listBoxClasses,
    gridListItemClasses = _useMemo.gridListItemClasses;

  // Handle single select initialization, this assumed non-controlled usage and
  // should only get set during initialization of the instance, it's dependencies
  // are not expected to change with-in the life cycle of the component.
  var _useMemo2 = useMemo(function () {
      var initValue = [];
      var defaultSelectedKey = undefined;
      if (isSingleSelectValue(defaultValue, multiple)) {
        defaultSelectedKey = defaultValue ? defaultValue.id : undefined;
        initValue = defaultSelectedKey ? [defaultSelectedKey] : initValue;
      }
      if (isMultiSelectValue(defaultValue, multiple)) {
        defaultSelectedKey = Array.isArray(defaultValue) ? defaultValue.map(function (i) {
          return i.id;
        }) : [];
        initValue = defaultSelectedKey;
      }
      return {
        defaultSelectedKey: defaultSelectedKey,
        initialSelectedKeys: initValue
      };
    }, [defaultValue, multiple]),
    defaultSelectedKey = _useMemo2.defaultSelectedKey,
    initialSelectedKeys = _useMemo2.initialSelectedKeys;
  var list = useListData({
    initialItems: items,
    initialSelectedKeys: initialSelectedKeys,
    getKey: getItemKey
  });
  var classes = useMemo(function () {
    return composeRenderProps(cn(baseClasses, _className), function (className) {
      return "csg-tw-select ".concat(className);
    });
  }, [baseClasses, _className]);
  var _useState = useState(!!_isOpen),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var handleOnOpenChange = useCallback(function (isOpen) {
    return setIsOpen(isOpen);
  }, []);
  var handleOnSelectionChange = useCallback(function (key) {
    var selection = Array.isArray(key) ? key.map(function (k) {
      return list.getItem(k);
    }) : key ? list.getItem(key) : null;
    // @ts-ignore
    onChange && onChange(selection);
  }, [list, onChange]);
  return /*#__PURE__*/React.createElement(_Select, _extends({}, props, {
    isOpen: isOpen,
    "data-testid": testId,
    className: classes,
    onOpenChange: handleOnOpenChange,
    multiple: multiple,
    defaultSelectedKey: defaultSelectedKey,
    onSelectionChange: handleOnSelectionChange
  }), label && /*#__PURE__*/React.createElement(Label, {
    className: labelClasses,
    ellipsis: "1"
  }, label), /*#__PURE__*/React.createElement(Button, {
    variant: variant === 'outlined' ? 'secondary' : 'ghost-secondary',
    className: buttonClasses,
    "data-testid": "".concat(testId, "-button"),
    onPress: noop
  }, /*#__PURE__*/React.createElement(_SelectValue, {
    className: "min-w-0 shrink grow placeholder-shown:text-on-action-disabled",
    renderSelectItems: renderSelectItems
  }), isOpen ? /*#__PURE__*/React.createElement(IconArrowCaretUp, {
    "aria-hidden": true,
    size: "1em",
    className: "text-neutral-strong"
  }) : /*#__PURE__*/React.createElement(IconArrowCaretDown, {
    "aria-hidden": true,
    size: "1em",
    className: "text-neutral-strong"
  })), description && /*#__PURE__*/React.createElement(Description, {
    className: subTextClasses,
    ellipsis: "1"
  }, description), /*#__PURE__*/React.createElement(FieldError, {
    className: subTextClasses
  }, errorMessage), /*#__PURE__*/React.createElement(PopoverCore, {
    className: cn('min-w-[--trigger-width]', {
      'shadow shadow-primary': legacyShadow
    }),
    trigger: variant === 'outlined' || variant === 'ghost' || variant === 'ghost-sm' || variant === 'ghost-xs' ? 'Dropdown' : undefined,
    offset: 4
  }, /*#__PURE__*/React.createElement(GridList, {
    items: list.items,
    className: listBoxClasses,
    selectedKeys: list.selectedKeys
  }, function (item) {
    return /*#__PURE__*/React.createElement(GridListItem, {
      id: item.id,
      className: gridListItemClasses,
      "data-testid": "".concat(testId, "-option-").concat(item.id)
    }, multiple ? item.label : /*#__PURE__*/React.createElement(Text, {
      slot: "label",
      ellipsis: "1"
    }, item.label));
  })));
}

export { Select as default, defaultRenderSelectItems };
//# sourceMappingURL=select.esm.js.map
